import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ClipboardListIcon, FileSignatureIcon, CompassIcon, RadarIcon, ListChecksIcon, HandshakeIcon, ClockIcon, InfoIcon } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useEscaCopy } from '../../i18n/escaCopy';
const icons = [<ClipboardListIcon className="w-6 h-6" data-id="element-713" data-mp="3oqlhh6jtt7zn" />, <FileSignatureIcon className="w-6 h-6" data-id="element-714" data-mp="3jsbp13phklhq" />, <CompassIcon className="w-6 h-6" data-id="element-715" data-mp="3jtd3li5pnq47" />, <RadarIcon className="w-6 h-6" data-id="element-716" data-mp="3f1224avbnw9j" />, <ListChecksIcon className="w-6 h-6" data-id="element-717" data-mp="3f00njwf3krn2" />, <HandshakeIcon className="w-6 h-6" data-id="element-718" data-mp="3eyz1kwwjmkqk" />];
export function ClassicProcess() {
  const {
    lang
  } = useLanguage();
  const c = useEscaCopy(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section id="process" className="relative py-20 lg:py-28 bg-white overflow-hidden" data-id="element-719" data-mp="30zzcxp9165ue">
      <div className="absolute inset-0 pointer-events-none" data-id="element-720" data-mp="2w3irj2t7u5l0">
        <div className="absolute top-1/2 -translate-y-1/2 left-0 w-[500px] h-[500px] rounded-full bg-primary-50/30 blur-3xl hidden sm:block" data-id="element-721" data-mp="2rfcbn4p1sdwi" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref} data-id="element-722" data-mp="2w7vr677apj3y">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} className="text-center mb-14 lg:mb-16" data-id="element-723" data-mp="2id332kdcaulr">
          <span className="text-sm font-semibold text-primary-600 tracking-wide uppercase" data-id="element-724" data-mp="3f5gbr2311vir">
            {lang === 'EN' ? 'Process' : 'Як ми працюємо'}
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-900 tracking-tight max-w-4xl mx-auto" data-id="element-725" data-mp="315ferxpvv88k">
            {c.process.title}
          </h2>
          <p className="mt-4 text-lg text-navy-500 max-w-2xl mx-auto leading-relaxed" data-id="element-726" data-mp="1pph1tirl4222">
            {c.process.lead}
          </p>
        </motion.div>

        {/* Desktop timeline */}
        <div className="hidden lg:block relative" data-id="element-727" data-mp="1anqlmep6govn">
          <motion.div initial={{
          scaleX: 0
        }} animate={isInView ? {
          scaleX: 1
        } : {}} transition={{
          duration: 1.1,
          delay: 0.25,
          ease: 'easeOut'
        }} className="absolute top-12 left-[8%] right-[8%] h-1 bg-gradient-to-r from-primary-300 via-primary-500 to-primary-300 origin-left rounded-full" data-id="element-728" data-mp="1apl65rwjeez7" />
          <div className="grid grid-cols-6 gap-4" data-id="element-729" data-mp="rx96jcx9prsr">
            {c.process.steps.map((step, i) => <motion.div key={step} initial={{
            opacity: 0,
            y: 30
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.5,
            delay: 0.2 + i * 0.1
          }} className="flex flex-col items-center text-center" data-id="element-730" data-mp="mwuyhuvo2huq" data-mpi={i}>
                <div className="relative z-10" data-id="element-731" data-mp="2d90tb3hwiddc">
                  <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white shadow-lg shadow-primary-500/25" data-id="element-732" data-mp="28b92kcszsxaq">
                    {icons[i]}
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 rounded-lg bg-white border-2 border-primary-200 flex items-center justify-center shadow-sm" data-id="element-733" data-mp="2kstfy0544ca3">
                    <span className="text-xs font-bold tabular-nums text-primary-700" data-id="element-734" data-mp="2ujeks5bdqd1g">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                  </div>
                </div>
                <h3 className="mt-5 text-base font-bold text-navy-900" data-id="element-735" data-mp="21uh426mtgoat">
                  {step}
                </h3>
              </motion.div>)}
          </div>
        </div>

        {/* Mobile vertical */}
        <div className="lg:hidden space-y-3" data-id="element-736" data-mp="2awqaoleyrxix">
          {c.process.steps.map((step, i) => <motion.div key={step} initial={{
          opacity: 0,
          x: -16
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.45,
          delay: 0.1 + i * 0.07
        }} className="flex items-center gap-4 p-4 rounded-2xl bg-white border border-navy-200 shadow-sm" data-id="element-737" data-mp="3i8785ddxrckg" data-mpi={i}>
              <div className="flex-shrink-0 inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 text-white" data-id="element-738" data-mp="3fwyc9x1dn7nl">
                {icons[i]}
              </div>
              <div data-id="element-739" data-mp="7n54wnus0gv5">
                <span className="text-xs font-bold tabular-nums text-primary-500" data-id="element-740" data-mp="7mn3lct9bcgd">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <h3 className="text-base font-bold text-navy-900" data-id="element-741" data-mp="2lzamllgwlji">{step}</h3>
              </div>
            </motion.div>)}
        </div>

        {/* Time block */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.35
      }} className="mt-14 lg:mt-16 grid lg:grid-cols-[auto_1fr] gap-6 lg:gap-12 items-center p-7 lg:p-10 rounded-2xl bg-navy-50/70 border border-navy-200" data-id="element-742" data-mp="cedfhn7ue89w">
          <div className="flex items-center gap-5" data-id="element-743" data-mp="19zg0v5e0mv5u">
            <div className="flex-shrink-0 inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 text-white shadow-lg shadow-primary-500/25" data-id="element-744" data-mp="19y4ztdbyg1if">
              <ClockIcon className="w-6 h-6" data-id="element-745" data-mp="10xf2u6ayk534" />
            </div>
            <div data-id="element-746" data-mp="m9byf1ckd1gf">
              <div className="text-4xl lg:text-5xl font-extrabold text-navy-900 tracking-tight leading-none" data-id="element-747" data-mp="m8srsgsd5zhp">
                {c.process.timeValue}
              </div>
              <div className="mt-2 text-sm text-navy-500" data-id="element-748" data-mp="d9ffzhgroytt">
                {c.process.timeLabel}
              </div>
            </div>
          </div>
          <div className="flex items-start gap-3 lg:border-l lg:border-navy-200 lg:pl-12" data-id="element-749" data-mp="hbafqhj1v45h">
            <InfoIcon className="flex-shrink-0 w-4 h-4 mt-0.5 text-navy-400" data-id="element-750" data-mp="3carhazg8r9u" />
            <p className="text-sm text-navy-500 leading-relaxed max-w-xl" data-id="element-751" data-mp="8ac8zrh88ij7">
              {c.process.timeCaveat}
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
}